import * as React from "react";
import {
  UserMenu,
  MenuItemLink,
  useGetIdentity,
  Logout,
  useUserMenu,
  useLogout
} from "react-admin";
import { Typography, Box } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import { UserAvatarIcon } from "./UserAvatarIcon";

function UserMenuIcon() {
  const { identity, isLoading } = useGetIdentity();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <UserAvatarIcon size={30} />

      <Typography
        variant="body2"
        sx={{
          fontWeight: 800,
          display: { xs: "none", sm: "block" },
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 180,
        }}
      >
        {isLoading || !identity
          ? ""
          : (identity as any)?.display_name ??
            (identity as any)?.fullName ??
            "User"}
      </Typography>
    </Box>
  );
}

/**
 * Tries to close the react-admin UserMenu across versions.
 * - Prefer a context close handler if present
 * - Fallback to a clickaway (Material UI Menu closes on clickaway)
 */
function useCloseUserMenu() {
  const ctx = useUserMenu(); // type: UserMenuContextValue | undefined (depends on your RA version)

  return React.useCallback(() => {
    // 1) best-effort: close handler from context (exists in some versions at runtime)
    const maybeOnClose = (ctx as any)?.onClose ?? (ctx as any)?.close;
    if (typeof maybeOnClose === "function") {
      maybeOnClose();
      return;
    }

    // 2) fallback: trigger clickaway so MUI Menu closes
    // (delay a tick so navigation handlers can run first)
    setTimeout(() => {
      try {
        (document.activeElement as HTMLElement | null)?.blur?.();
      } catch {}
      document.body.click();
    }, 0);
  }, [ctx]);
}

function ProfileMenuItem() {
  const closeMenu = useCloseUserMenu();

  return (
    <MenuItemLink
      to="/profile"
      primaryText="Profil"
      leftIcon={<PersonIcon />}
      onClick={closeMenu}
    />
  );
}

function LogoutMenuItem() {
    const logout = useLogout();
    const closeMenu = useCloseUserMenu();
    const handleClick = (event: React.MouseEvent) => {
      closeMenu(); // optional: erst Menü zu
      logout();    // dann wirklich ausloggen
    // alternativ: logout({}, "/login") oder logout({}, false) je nach RA-Version
    };

  return <Logout icon={<LogoutIcon />} onClick={handleClick} />;
}

export function CustomUserMenu() {
  const { identity, isLoading } = useGetIdentity();

  const menuKey = isLoading
    ? "loading"
    : `${(identity as any)?.id ?? "anon"}:${
        (identity as any)?.avatar_url ?? "noavatar"
      }`;

  return (
    <UserMenu key={menuKey} icon={<UserMenuIcon />}>
      <ProfileMenuItem />
      <LogoutMenuItem />
    </UserMenu>
  );
}
